import React from 'react';
import { CardType, Translation } from '../../interfaces/cards';
import { Deck } from '../../interfaces/decks';
import MaterialDropdown from '../materialDropdown/MaterialDropdown';
import MaterialMultiSelect from '../materialMultiSelect/MaterialMultiSelect';
import VoiceSelection from '../voiceSelection/VoiceSelection';
import './AddCardBottomPanel.css';

interface AddCardBottomPanelProps {
  // Deck related
  deckId: number | undefined;
  decks: Deck[];
  onDeckChange: (id: string) => void;

  // State flags
  translations: Translation[];
  manualTargetPhrase: string;

  // Actions
  onAddCards: () => void;

  // Voice selection
  deck: Deck | null;
  voiceText: string | undefined;
  onVoiceChange: (voiceId: string) => void;
  onSpeedChange: (speed: number) => void;

  // Card types
  cardTypes: CardType[];
  selectedCardTypeIds: number[];
  onCardTypeChange: (selectedIds: number[]) => void;

  // Input mode
  inputMode?: 'auto' | 'manual' | 'media' | 'topic';
  generatedCards?: Array<{
    text: string;
    translation: string;
    selected: boolean;
    id: number;
  }>;
}

const AddCardBottomPanel: React.FC<AddCardBottomPanelProps> = ({
  deckId,
  decks,
  onDeckChange,
  translations,
  manualTargetPhrase,
  onAddCards,
  deck,
  voiceText,
  onVoiceChange,
  onSpeedChange,
  cardTypes,
  selectedCardTypeIds,
  onCardTypeChange,
  inputMode = 'auto',
  generatedCards = [],
}) => {
  return (
    <div className="bottom-action-bar">
      <div className="bottom-action-content">
        {/* First row: Deck selection + Card types side by side */}
        <div className="bottom-action-row">
          <div className="deck-selection-container">
            <MaterialDropdown
              label="Deck"
              value={deckId || ''}
              options={decks.map((deck) => ({
                value: deck.id,
                label: deck.name,
              }))}
              onChange={(value) => onDeckChange(value)}
              size="small"
              fullWidth={false}
            />
          </div>
          <div className="card-type-selection-container">
            <MaterialMultiSelect
              label="Card Types"
              value={selectedCardTypeIds}
              options={cardTypes.map((cardType) => ({
                value: cardType.id,
                label: cardType.name,
              }))}
              onChange={(values) => onCardTypeChange(values as number[])}
              disabled={cardTypes.length === 0}
              size="small"
              fullWidth={false}
            />
          </div>
        </div>

        {/* Second row: Voice selection + Add Card button */}
        <div className="bottom-action-bottom">
          <VoiceSelection
            key={voiceText || 'empty'}
            languageCode={deck?.language_code}
            text={voiceText}
            disabled={
              inputMode === 'media' || inputMode === 'topic'
                ? !generatedCards.some((c) => c.selected)
                : inputMode === 'manual'
                  ? !manualTargetPhrase
                  : !translations.some((t) => t.selected)
            }
            onVoiceChange={onVoiceChange}
            onSpeedChange={onSpeedChange}
          />
          <div className="add-card-button-container">
            <button
              onClick={onAddCards}
              className="phn-button add-cards-button"
              disabled={
                inputMode === 'media' || inputMode === 'topic'
                  ? !generatedCards.some((c) => c.selected)
                  : inputMode === 'manual'
                    ? !manualTargetPhrase
                    : !translations.some((t) => t.selected)
              }
            >
              Add Card
              {inputMode === 'media' || inputMode === 'topic'
                ? generatedCards.filter((c) => c.selected).length > 1
                  ? 's'
                  : ''
                : inputMode === 'auto' &&
                    translations.filter((t) => t.selected).length > 1
                  ? 's'
                  : ''}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCardBottomPanel;
